import type { HomeSlice } from '../bs/home'

/**
 * English for the two home screens.
 *
 * "Report" is the noun throughout, matching the enum labels the server sends
 * for `submissionTypes` — an English-speaking auditor reading "Breakdown or
 * incident" on a badge should meet the same word in the heading above it.
 */
export const home: HomeSlice = {
  'home.greeting': 'Good day, {name}',

  'home.report.title': 'Report a problem',
  'home.report.text': 'A breakdown, an incident, a safety issue or an improvement',
  'home.scan.title': 'Scan a QR code',
  'home.scan.text': 'Open a report for the machine you are standing at',

  'home.mine.title': 'My reports',
  'home.mine.all': 'All mine →',
  'home.stat.inProgress': 'In progress',
  'home.mine.resolved': 'Resolved',
  'home.recent.empty': 'You have not sent a report yet',
  'home.recent.emptyText': 'Once you report a fault or send an idea, it appears here with its status.',

  'home.rating.title': 'My rating this month',
  'home.rating.empty': 'The rating appears after your first walk or task of the month.',
  'home.rating.noWalks': 'No walk yet',
  'home.rating.noTasks': 'No tasks assigned',
  'home.rating.tasksDone': '{done} of {total} finished',
  'home.rating.proposals': 'Proposals',
  'home.rating.accepted.one': '{count} accepted',
  'home.rating.accepted.few': '{count} accepted',
  'home.rating.accepted.many': '{count} accepted',
  'home.rating.points': '{points} points in all',

  'home.today': 'For you today',

  'home.stat.openSubmissions.explain': 'Every open report in the plants you can see, not only your own.',
  'home.stat.overdue': 'Overdue',
  'home.stat.overdue.needsAttention': 'Needs attention today',
  'home.stat.overdue.allOnTime': 'All on time',
  'home.stat.overdue.explain':
    'Open reports whose due date has passed; reports with no due date are not counted here.',
  'home.stat.fresh': 'New',
  'home.stat.fresh.hint': 'Waiting for a first look',
  'home.stat.unassigned': 'Unassigned',
  'home.stat.unassigned.hint': 'Of {open} open',
  'home.stat.myTasks': 'My tasks',
  'home.stat.myTasks.explain': 'Tasks assigned to you that are not finished yet.',

  'home.overdue.title': 'Longest overdue',
  'home.overdue.all': 'All overdue ({count}) →',
  'home.overdue.empty': 'Nothing is overdue',
  'home.overdue.emptyText': 'Reports that miss their due date appear here, oldest first.',
  'home.overdue.nobody': 'unassigned',
  'home.overdue.late.one': '{count} day late',
  'home.overdue.late.few': '{count} days late',
  'home.overdue.late.many': '{count} days late',

  'home.fiveS.month': '5S · {month}',
  'home.fiveS.board': '5S board →',
  'home.fiveS.walkedOf.one': 'of {count} machine walked',
  'home.fiveS.walkedOf.few': 'of {count} machines walked',
  'home.fiveS.walkedOf.many': 'of {count} machines walked',
  'home.fiveS.progress': 'Machines walked this month',
  'home.fiveS.average': 'Average of this month’s walks: {value}',
  'home.fiveS.allWalked': 'Every machine has been walked this month.',
  'home.fiveS.toWalk': 'Not walked yet',
  'home.fiveS.more': '+{count} on the 5S board →',
  'home.fiveS.noMachines': 'There are no active machines in your plants.',

  'home.activity.title': 'Lately in your plant',
  'home.activity.title.several': 'Lately in your plants',
  'home.activity.title.all': 'Lately in every plant',
  'home.activity.empty': 'Nothing has happened yet',
  'home.activity.emptyText': 'New reports, status changes and 5S walks appear here.',
  'home.activity.reported': 'New report {ref}',
  'home.activity.moved': 'Status change {ref}',
  'home.activity.walked': '5S walk',

  'home.tasks.title': 'My tasks',
  'home.tasks.all': 'All tasks →',
  'home.tasks.due': 'Due: {date}',
  'home.tasks.noDue': 'No due date',
  'home.tasks.overdue': 'Overdue',
  'home.tasks.lateCount.one': '{count} overdue',
  'home.tasks.lateCount.few': '{count} overdue',
  'home.tasks.lateCount.many': '{count} overdue',
  'home.tasks.soonCount.one': '{count} due this week',
  'home.tasks.soonCount.few': '{count} due this week',
  'home.tasks.soonCount.many': '{count} due this week',
  'home.tasks.nothingSoon': 'Nothing urgent this week',
  'home.tasks.empty': 'You have no open tasks',
  'home.tasks.emptyText': 'Tasks somebody assigns to you on a report appear here.',

  'home.fiveS.plant': 'Plant average',
  'home.fiveS.explain':
    'The average beside it is there so your own figure has something to be read against — it is worked out from the plants you can see, it is not a ranking and it says nothing about your place.',

  'leaderboard.title': 'Ranking',
  'leaderboard.formula':
    'Base: 5S {fiveS} and finished tasks {tasks}. Accepted proposals add up to +{bonus} ({accepted} accepted for the full bonus) — proposals can only raise a rating, never lower it.',
  'leaderboard.explain.note':
    'The 5S half describes the machines somebody has looked after, not the person — anybody moved onto a neglected centre reads low for exactly that reason. Accepted proposals are added on top of the base and can only raise it: a refused proposal takes nothing away. Somebody who was on no walk, or who was given no task, is rated on the half they do have, and the missing half does not count as a nought.',
  'leaderboard.explain.ties':
    'The same score shares a place: two people on 82 are both second, and the next one is fourth.',
  'leaderboard.part.fiveS': '5S',
  'leaderboard.part.bonus': '+{bonus} for {accepted} accepted',
  'leaderboard.part.followThrough': 'Tasks',
  'leaderboard.you': '(you)',
  'leaderboard.thin': 'few walks',
  'leaderboard.showAll': 'Show all ({count})',
  'leaderboard.showLess': 'Show fewer',
  'leaderboard.rating': 'Rating',
  'leaderboard.position': 'Place',

  'home.fiveS.plant.several': 'Average of your plants',
  'home.fiveS.plant.all': 'Company average',
}
